import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import styled from "styled-components";

const TRANSITION_EFFECT = "all 0.3s ease-in";
const PANEL_BACKGROUND = "#2f4050";
const EAR_SIZE = 50;

const EdgePanelContext = createContext(null);

// Coluna encostada na borda direita: cada painel aberto empurra a própria orelha
// para a esquerda e as orelhas fechadas descem sozinhas. É o empilhamento que
// substitui os `top` fixos que cada painel calculava por conta.
const Column = styled.div`
  transition: ${TRANSITION_EFFECT};
  position: fixed;
  right: 0px;
  top: ${({ top }) => top};
  max-height: calc(100vh - ${({ top }) => top} - 20px);
  z-index: 2023;
  display: ${({ hidden }) => (hidden ? "none" : "flex")};
  flex-direction: column;
  align-items: flex-end;
  pointer-events: none;
`;

// Fechado, o dock inteiro fica deslocado para a direita pela largura do corpo,
// então só a orelha sobra na borda e abrir traz orelha e painel juntos. Como o
// dock mantém a largura do painel mesmo fechado, o pointer-events precisa ficar
// na orelha e no corpo, senão ele captura clique na faixa vazia da tela.
const Dock = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  pointer-events: none;
  transition: transform 0.3s ease-in;
  transform: translateX(${({ open, width }) => (open ? 0 : width)}px);

  & + & {
    margin-top: 10px;
  }
`;

const Ear = styled.button`
  order: 1;
  position: relative;
  flex: 0 0 ${EAR_SIZE}px;
  width: ${EAR_SIZE}px;
  height: ${EAR_SIZE}px;
  padding: 0px;
  border: 0px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  background-color: ${PANEL_BACKGROUND};
  border-radius: 10px 0 0 10px;
  box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.8);
  cursor: pointer;
  pointer-events: auto;

  /* Esconde a emenda entre orelha e painel sem deixar a sombra atravessar. */
  clip-path: inset(0px 0px -12px -12px);

  &:focus {
    outline: none;
  }
`;

// Ponto de atenção na orelha, visível com o painel fechado (ex.: algum silo
// abastecendo). Fica fora do clip-path da emenda para não ser cortado.
const EarBadge = styled.span`
  position: absolute;
  top: 5px;
  right: 7px;
  width: 15px;
  height: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background-color: #ff9500;
  color: #3d2a00;
  font-family: "Rubik", sans-serif;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.45);
`;

// A altura vai a zero para o painel fechado não ocupar espaço na coluna e não
// empurrar as orelhas de baixo. Aberto, vai medida em pixels, e não em auto,
// porque auto não interpola: o corpo sumiria de uma vez e a orelha deslizaria
// sozinha. Sem medida disponível cai para auto, que não anima mas nunca corta.
const bodyHeight = ({ open, height }) => {
  if (!open) return "0px";
  return height === null ? "auto" : `${height}px`;
};

const Body = styled.div`
  order: 2;
  pointer-events: auto;
  transition: height 0.3s ease-in;
  overflow: hidden;
  width: ${({ width }) => width}px;
  height: ${bodyHeight};
  background-color: ${PANEL_BACKGROUND};
  color: #fff;
  border-radius: 0 0 0 10px;
  box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.8);
  clip-path: inset(0px 0px -12px -12px);
`;

// Largura fixa para o conteúdo não refluir enquanto o painel desliza.
const BodyContent = styled.div`
  width: ${({ width }) => width}px;
  padding: 10px 14px 14px;
`;

const Title = styled.h5`
  margin: 0px 0px 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  font-family: "Rubik", sans-serif;
  font-size: 13px;
  font-weight: 400;
  letter-spacing: 0.08em;
  text-align: center;
  text-transform: uppercase;
  white-space: nowrap;
`;

/**
 * Guarda qual painel da borda está aberto. Só um por vez: abrir um fecha o que
 * estava aberto.
 */
export const EdgePanelGroup = ({ top = "120px", hidden = false, children }) => {
  const [openId, setOpenId] = useState(null);

  const toggle = (id) => setOpenId(current => (current === id ? null : id));

  // O menu de controle ocupa a mesma borda e esconde a coluna; fecha o que
  // estava aberto para a coluna não voltar com um painel aberto por trás dele.
  useEffect(() => {
    if (hidden) setOpenId(null);
  }, [hidden]);

  return (
    <EdgePanelContext.Provider value={{ openId, toggle }}>
      <Column top={top} hidden={hidden}>
        {children}
      </Column>
    </EdgePanelContext.Provider>
  );
};

export const useEdgePanelGroup = () => useContext(EdgePanelContext);

/**
 * Orelha + painel deslizante. O componente só cuida do abre e fecha; o conteúdo
 * vem inteiro de quem usa.
 */
const EdgePanel = ({
  id,
  icon,
  title,
  width = 230,
  alert = false,
  children,
}) => {
  const group = useEdgePanelGroup();
  const open = group ? group.openId === id : false;
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(null);

  // O conteúdo cresce por estado interno do filho — o silo que revela os valores
  // ao ser clicado — e nesse caso o EdgePanel não re-renderiza, porque o React
  // re-renderiza a partir de onde o estado mudou. Então quem avisa da mudança de
  // altura é o ResizeObserver, e não o ciclo de render, senão o corpo fica com a
  // altura de antes do clique e o overflow corta o que apareceu.
  useLayoutEffect(() => {
    const node = contentRef.current;
    // Sem ResizeObserver a altura fica em null de propósito: o corpo cai para
    // auto, que perde a animação de fechamento mas nunca corta o conteúdo.
    if (!node || typeof ResizeObserver === "undefined") return undefined;

    const measure = () => setContentHeight(
      current => (current === node.offsetHeight ? current : node.offsetHeight)
    );

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Dock open={open} width={width}>
      <Ear
        type="button"
        aria-expanded={open}
        title={title}
        onClick={() => group && group.toggle(id)}
      >
        {icon}
        {alert && <EarBadge>!</EarBadge>}
      </Ear>

      <Body open={open} width={width} height={contentHeight}>
        <BodyContent width={width} ref={contentRef}>
          {title && <Title>{title}</Title>}
          {children}
        </BodyContent>
      </Body>
    </Dock>
  );
};

export default EdgePanel;
